import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { normalizeLanguage, SupportedLanguage } from '../i18n';
import {
    getDefaultShareImageUrl,
    getPageUrl,
    upsertCanonicalLink,
    upsertMetaTag,
} from '../utils/documentMeta';

const OG_LOCALE: Record<SupportedLanguage, string> = {
    en: 'en_US',
    ar: 'ar_SA',
    he: 'he_IL',
};

/**
 * Updates document title and meta tags for the landing page when language changes.
 * Static fallbacks live in public/index.html for crawlers with limited JS.
 */
export const useLandingPageMeta = (): void => {
    const { t, i18n } = useTranslation();

    useEffect(() => {
        const lang = normalizeLanguage(i18n.language);
        const title = t('seo.title');
        const description = t('seo.description');
        const keywords = t('seo.keywords');
        const siteName = t('seo.siteName');
        const url = getPageUrl();
        const image = getDefaultShareImageUrl();

        document.title = title;

        upsertMetaTag('name', 'description', description);
        upsertMetaTag('name', 'keywords', keywords);
        upsertMetaTag('name', 'robots', 'index, follow');

        upsertMetaTag('property', 'og:type', 'website');
        upsertMetaTag('property', 'og:site_name', siteName);
        upsertMetaTag('property', 'og:title', title);
        upsertMetaTag('property', 'og:description', description);
        upsertMetaTag('property', 'og:url', url);
        upsertMetaTag('property', 'og:image', image);
        upsertMetaTag('property', 'og:locale', OG_LOCALE[lang]);

        upsertMetaTag('name', 'twitter:card', 'summary_large_image');
        upsertMetaTag('name', 'twitter:title', title);
        upsertMetaTag('name', 'twitter:description', description);
        upsertMetaTag('name', 'twitter:image', image);

        upsertCanonicalLink(url);
    }, [t, i18n.language]);
};
